import React, { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, GitCompareArrows, RotateCcw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";


const severityStyle = {
  high: "border-red-500/40 bg-red-500/5 text-red-200",
  medium: "border-amber-500/40 bg-amber-500/5 text-amber-100",
};

function errorDetail(error, fallback) {
  const detail = error?.response?.data?.detail;
  return typeof detail === "string" ? detail : fallback;
}

export default function DealConsistencyPanel({ deal }) {
  const { t } = useI18n();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.getDealConsistency(deal.id)
      .then(setReport)
      .catch(() => toast.error(t("consistency_load_failed")))
      .finally(() => setLoading(false));
  }, [deal.id, t]);

  const runCheck = async () => {
    setRunning(true);
    try {
      const created = await api.runDealConsistency(deal.id);
      setReport(created);
      toast.success(created.counts.open ? t("consistency_found").replace("{count}", created.counts.open) : t("consistency_clear"));
    } catch (error) {
      toast.error(errorDetail(error, t("consistency_run_failed")));
    } finally {
      setRunning(false);
    }
  };

  const updateDiscrepancy = async (item, status) => {
    let note = null;
    if (status !== "open") {
      note = window.prompt(status === "resolved" ? t("consistency_resolve_prompt") : t("consistency_accept_prompt"));
      if (note === null) return;
      if (!note.trim()) {
        toast.error(t("consistency_note_required"));
        return;
      }
    }
    setUpdatingId(item.id);
    try {
      const updated = await api.updateDealDiscrepancy(deal.id, report.id, item.id, { status, note });
      setReport(updated);
      toast.success(t("consistency_status_updated"));
    } catch (error) {
      toast.error(errorDetail(error, t("consistency_update_failed")));
    } finally {
      setUpdatingId(null);
    }
  };

  if (loading) return <div className="card-surface p-8 text-[var(--td-text-3)]">…</div>;
  const outdated = report && Number(report.deal_master_revision || 0) < Number(deal.master_data_revision || 0);

  return (
    <div className="max-w-5xl space-y-5" data-testid="deal-consistency-panel">
      <div className="card-surface p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <div className="kicker mb-2">— {t("consistency_kicker")}</div>
            <h2 className="font-serif text-2xl sm:text-3xl flex items-center gap-2">
              <GitCompareArrows size={20} className="text-[var(--td-gold)]" />{t("consistency_title")}
            </h2>
            <p className="text-sm text-[var(--td-text-2)] mt-2 max-w-3xl leading-relaxed">{t("consistency_intro")}</p>
          </div>
          <button type="button" onClick={runCheck} disabled={running}
            className="btn-gold inline-flex items-center justify-center gap-2 disabled:opacity-50 whitespace-nowrap min-h-11">
            {running ? <RotateCcw size={14} className="animate-spin" /> : <GitCompareArrows size={15} />}
            {running ? t("consistency_running") : t("consistency_run")}
          </button>
        </div>
        <p className="mt-4 text-[11px] text-[var(--td-text-3)]">{t("consistency_scope")}</p>
      </div>

      {!report ? (
        <div className="card-surface p-10 text-center">
          <ShieldCheck size={30} className="mx-auto text-[var(--td-gold)] mb-3" />
          <div className="font-serif text-2xl">{t("consistency_empty")}</div>
          <p className="text-sm text-[var(--td-text-3)] mt-2">{t("consistency_empty_hint")}</p>
        </div>
      ) : (
        <>
          {outdated && (
            <div className="border border-amber-500/40 bg-amber-500/5 p-4 text-sm text-amber-200 flex items-start gap-2">
              <AlertTriangle size={17} className="shrink-0 mt-0.5" />{t("consistency_outdated")}
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Metric value={report.documents_count} label={t("consistency_documents")} />
            <Metric value={report.counts.total} label={t("consistency_total")} />
            <Metric value={report.counts.open} label={t("consistency_open")} tone={report.counts.open ? "text-amber-300" : "text-emerald-300"} />
            <Metric value={report.counts.high} label={t("consistency_high")} tone="text-red-300" />
          </div>

          {report.discrepancies.length === 0 ? (
            <div className="card-surface p-10 text-center text-emerald-300">
              <CheckCircle2 size={26} className="mx-auto mb-2" />{t("consistency_clear")}
            </div>
          ) : (
            <div className="space-y-4">
              {report.discrepancies.map((item) => (
                <article key={item.id} className={`border p-4 sm:p-5 ${severityStyle[item.severity] || severityStyle.medium} ${item.status !== "open" ? "opacity-70" : ""}`}>
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className="font-mono text-[9px] uppercase tracking-widest border border-current px-1.5 py-0.5">{t(`consistency_severity_${item.severity}`)}</span>
                        <span className="font-mono text-[9px] uppercase tracking-widest opacity-75">{t(`consistency_status_${item.status}`)}</span>
                      </div>
                      <h3 className="font-serif text-xl sm:text-2xl">{item.label}</h3>
                      <p className="text-sm opacity-85 mt-1">{item.description}</p>
                    </div>
                    <div className="flex flex-wrap gap-2 shrink-0">
                      {item.status === "open" ? <>
                        <button type="button" disabled={updatingId === item.id} onClick={() => updateDiscrepancy(item, "resolved")} className="border border-current px-3 py-2 font-mono text-[9px] uppercase tracking-widest hover:text-emerald-300 disabled:opacity-40">{t("consistency_resolve")}</button>
                        <button type="button" disabled={updatingId === item.id} onClick={() => updateDiscrepancy(item, "accepted")} className="border border-current px-3 py-2 font-mono text-[9px] uppercase tracking-widest disabled:opacity-40">{t("consistency_accept")}</button>
                      </> : (
                        <button type="button" disabled={updatingId === item.id} onClick={() => updateDiscrepancy(item, "open")} className="border border-current px-3 py-2 font-mono text-[9px] uppercase tracking-widest disabled:opacity-40">{t("consistency_reopen")}</button>
                      )}
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-2 mt-4">
                    {item.values.map((source, index) => (
                      <div key={`${source.source_type}-${source.review_id || "master"}-${index}`} className="border border-current/20 bg-black/10 p-3 min-w-0">
                        <div className="font-mono text-[9px] uppercase tracking-widest opacity-70 truncate">
                          {source.source_type === "deal_master" ? t("consistency_deal_master") : source.file_name}
                          {source.confidence && source.confidence !== "confirmed" ? ` · ${t(`review_import_confidence_${source.confidence}`)}` : ""}
                        </div>
                        <div className="font-serif text-lg mt-1 break-words">{source.value}</div>
                        {source.evidence && <div className="text-[11px] italic opacity-65 mt-1 break-words">“{source.evidence}”</div>}
                      </div>
                    ))}
                  </div>
                  {item.resolution_note && (
                    <div className="mt-3 border-l-2 border-current/40 pl-3 text-xs">
                      <span className="font-mono text-[9px] uppercase tracking-widest mr-2">{t("consistency_resolution")}</span>{item.resolution_note}
                    </div>
                  )}
                </article>
              ))}
            </div>
          )}
          <div className="text-[11px] text-[var(--td-text-3)]">
            {t("consistency_report_meta").replace("{date}", new Date(report.created_at).toLocaleString()).replace("{revision}", report.deal_master_revision)}
          </div>
        </>
      )}
    </div>
  );
}

function Metric({ value, label, tone = "" }) {
  return <div className="card-surface p-4"><div className={`font-serif text-3xl ${tone}`}>{value ?? "—"}</div><div className="font-mono text-[9px] uppercase tracking-widest text-[var(--td-text-3)] mt-1">{label}</div></div>;
}
